import { Box, Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useMember } from "api/members";
import { useWallets } from "api/wallets";
import { useAppSelector } from "app/hooks";
import { BoxLabel, BoxValue } from "components/BoxSection/BoxSection";
import { ReactComponent as ChartBreakoutCircleIcon } from "components/Icons/chart-breakout-circle.svg";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as RefreshCcw03Icon } from "components/Icons/refresh-ccw-03.svg";
import { ReactComponent as Trophy01Icon } from "components/Icons/trophy-01.svg";
import { ReactComponent as UserDown01Icon } from "components/Icons/user-down-01.svg";
import { ReactComponent as UserUp01Icon } from "components/Icons/user-up-01.svg";
import { ChartBox } from "components/Layout/Member/SingleMember/UserCard/ChartBox";
import { BarChartPercentage } from "components/Table/BarChartPercentage";
import { OLTabs } from "components/Tabs/OLTabs";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { addDays, format } from "date-fns";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useParams } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { MembersStatus, MemberTierSet } from "types/members";
import { SettingsModel } from "types/settings";
import { TierInTierSetType, TierSetType, TierType } from "types/tierSets";
import { alphabetizeByProperty } from "utilities/arrays";
import { OLDate } from "utilities/dateFormat";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    container: {
      borderRadius: 8,
      borderTop: `1px solid ${theme.gray50}`,
      display: "flex",
      rowGap: 0,
    },
    item: {
      margin: theme.spacing(4, 0),
      padding: theme.spacing(0, 8),
      borderRight: `0.5px solid ${theme.gray200}`,
      "&:nth-of-type(5)": { borderRight: "none" },
    },
    divider: {
      borderBottom: `1px solid ${theme.gray200}`,
      width: "100%",
    },
    label: {
      alignItems: "center",
      color: theme.gray500,
      display: "flex",
      fontSize: 14,
      fontWeight: 400,
      gap: theme.spacing(2),
      lineHeight: "20px",
    },
    tooltip: {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    tooltipIcon: {
      color: theme.gray500,
      height: "16px",
      marginLeft: "auto",
      width: "16px",
    },
    value: {
      color: theme.gray800,
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "24px",
      marginTop: theme.spacing(4),
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    chartsWrapper: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      marginBottom: theme.spacing(5),
      [theme.breakpoints.up("md")]: {
        flexDirection: "row",
      },
    },
    textWrapper: {
      maxWidth: "50%",
      overflow: "hidden",
      textOverflow: "ellipsis",

      "& div": {
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    icon: {
      color: theme.gray500,
      height: "16px",
      width: "16px",
    },
    tiersHeaderWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(12),
      marginBottom: theme.spacing(4),
    },
    flexWrapper: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(4),
      justifySelf: "end",
      maxWidth: "100%",
    },
    conditionsSection: {
      padding: theme.spacing(0, 8),
    },
    conditionItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(9, 0),
      borderBottom: `1px solid ${theme.gray100}`,
    },
    conditionValueWrapper: {
      display: "flex",
      alignItems: "center",
    },
    boldFont: {
      fontWeight: 600,
    },
    typography: {
      fontSize: "14px",
      lineHeight: "20px",
      color: theme.gray800,
    },
    barChart: {
      height: "12px",
      width: "100px",
    },
    gap: {
      gap: theme.spacing(4),
    },
    showAllBtn: {
      gap: theme.spacing(2.5),
      padding: 0,
      marginTop: theme.spacing(5),
    },
    addIcon: {
      width: "16px",
      height: "16px",
    },

    digitalWalletButton: {
      fontSize: 15,
      color: theme.primary500,
    },

    unitTitle: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: "20px",
      color: theme.gray500,
    },
    unitValue: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: "24px",
      color: theme.gray800,
    },

    tooltipTitle: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "24px",
      color: theme.gray800,
    },
    tooltipDesc: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "20px",
      marginTop: theme.spacing(2),
    },
    header: {
      color: theme.gray800,
      fontSize: 14,
      fontWeight: 500,
      lineHeight: "22px",
      margin: theme.spacing(12, 0, 8, 0),
      textTransform: "uppercase",
      textWrap: "nowrap",
    },
  }),
);

interface TierSetItem {
  id: string;
  label: string;
}

interface Props {
  membersStatus?: MembersStatus;
  settings: SettingsModel;
  tiersMode: any;
  selectedTierSetId: string | undefined;
  setSelectedTierSetId: Dispatch<SetStateAction<string | undefined>>;
  tierSetList: TierSetType[] | undefined;
  manuallyAssignedTier?: TierInTierSetType;
}

const VISIBLE_CONDITIONS_AMOUNT = 5;

export function TiersSection({
  membersStatus,
  settings,
  tiersMode,
  selectedTierSetId,
  setSelectedTierSetId,
  tierSetList,
  manuallyAssignedTier,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const { id: memberID } = useParams<{ id: string }>();

  const [renderedConditions, setRenderedConditions] = useState<
    MemberTierSet["nextTierCurrentProgress"] | undefined
  >([]);
  const [conditionsToRender, setConditionsToRender] = useState<
    MemberTierSet["nextTierCurrentProgress"] | undefined
  >([]);

  const { useMemberTierSet } = useMember(memberID);
  const { data: memberTierSet } = useMemberTierSet(selectedTierSetId as string);

  const { useWalletTypeList } = useWallets();
  const { data: walletTypeList } = useWalletTypeList();
  useEffect(() => {
    setRenderedConditions(
      memberTierSet?.nextTierCurrentProgress.slice(
        0,
        VISIBLE_CONDITIONS_AMOUNT,
      ),
    );
    setConditionsToRender(
      memberTierSet?.nextTierCurrentProgress.slice(VISIBLE_CONDITIONS_AMOUNT),
    );
  }, [tierSetList, memberTierSet]);

  const isNextTier = !!memberTierSet?.nextTierId;

  const findWalletName = (wallet: string) =>
    walletTypeList?.items?.find((walletType) => walletType.code === wallet)
      ?.name || "";

  const prepareConditionInputName = (attribute: string, wallet?: string) => {
    switch (attribute) {
      case "activeUnits":
        return `${message({ id: "activeUnits" })} (${findWalletName(
          wallet as string,
        )})`;
      case "totalEarnedUnits":
        return `${message({ id: "totalEarnedUnitsConst" })} (${findWalletName(
          wallet as string,
        )})`;
      case "monthsSinceJoiningProgram":
        return message({ id: "monthsSinceJoiningTheProgram" });
      case "totalSpending":
        return `${message({ id: "totalSpendingWithCurrency" }, { currency })}`;
      case "cumulatedEarnedUnits":
        return `${message({ id: "cumulativeEarnedUnits" })} (${findWalletName(wallet as string)})`;
      default:
        return message({ id: attribute });
    }
  };

  const prepareNextRecalculationDate = () => {
    if (memberTierSet?.downgrade === "automatic")
      return message({ id: "automatic" });
    else if (memberTierSet?.downgrade === "periodic")
      return OLDate(memberTierSet?.nextRecalculationAt, {
        formatString: "dd MMM, yyy",
      });
    else return "-";
  };

  const tierSetItems: TierSetItem[] = useMemo(
    () =>
      alphabetizeByProperty(
        tierSetList?.map(
          ({ tierSetId, name }: { tierSetId: string; name: string }) => ({
            id: tierSetId,
            label: name,
          }),
        ),
        "label",
      ),
    [tierSetList],
  );

  const tierSetButtonLabel = tierSetItems?.find(
    (tierSet) => tierSet.id === selectedTierSetId,
  )?.label;

  const tierSetTableData = [
    {
      dataTestId: "tier-set-value",
      labelDataTestId: "tier-set-label",
      icon: <Trophy01Icon className={classes.icon} />,
      label: memberTierSet?.tierSetName,
      value: memberTierSet?.currentTierName,
    },
    {
      dataTestId: "last-promotion-value",
      icon: <UserUp01Icon className={classes.icon} />,
      label: message({ id: "lastPromotion" }),
      value:
        OLDate(memberTierSet?.lastPromotionAt, {
          formatString: "dd MMM, yyy",
        }) || "-",
    },
    {
      dataTestId: "last-downgrade-value",
      icon: <UserDown01Icon className={classes.icon} />,
      label: message({ id: "lastDowngrade" }),
      value:
        OLDate(memberTierSet?.lastDowngradeAt, {
          formatString: "dd MMM, yyy",
        }) || "-",
    },
    {
      dataTestId: "next-recalculation-value",
      icon: <RefreshCcw03Icon className={classes.icon} />,
      label: message({ id: "nextRecalculation" }),
      value: manuallyAssignedTier ? "-" : prepareNextRecalculationDate(),
    },
    {
      dataTestId: "current-progress-value",
      icon: <ChartBreakoutCircleIcon className={classes.icon} />,
      label: message({ id: "currentProgress" }),
      tooltip: (
        <ExternalLinkTooltipContent
          name={
            manuallyAssignedTier ? "tierAssignedManually" : "currentProgress"
          }
          text={message({
            id: manuallyAssignedTier
              ? "manuallyAssignedTierDescription"
              : "currentProgressDescription",
          })}
        />
      ),
      value: (
        <Box className={classes.flexWrapper}>
          {manuallyAssignedTier ||
          typeof memberTierSet?.currentProgress !== "number" ? (
            <Typography>-</Typography>
          ) : (
            <>
              <Typography>
                {isNextTier ? memberTierSet?.currentProgress : 100}%
              </Typography>
              <BarChartPercentage
                barChartData={[
                  {
                    itemShare: isNextTier ? memberTierSet?.currentProgress : 1,
                  },
                  {
                    itemShare: isNextTier
                      ? 100 - memberTierSet?.currentProgress
                      : 0,
                  },
                ]}
                className={classes.barChart}
              />
            </>
          )}
        </Box>
      ),
    },
  ];

  const handleShowAllClick = () => {
    if (conditionsToRender!.length > 0) {
      const newConditionsToRender = [...conditionsToRender!];
      const fiveConditionsToShow = newConditionsToRender.splice(
        0,
        VISIBLE_CONDITIONS_AMOUNT,
      );
      setRenderedConditions((prevState) => [
        ...prevState!,
        ...fiveConditionsToShow,
      ]);
      setConditionsToRender(newConditionsToRender);
    }
  };

  if (!tiersMode) return null;

  return (
    <>
      <Typography mb={6} className={classes.header}>
        {message({ id: "tiers" })}
      </Typography>
      {tiersMode === TierType.TIER_SET ? (
        <>
          {selectedTierSetId ? (
            <>
              <Grid container className={classes.tiersHeaderWrapper}>
                {!!tierSetButtonLabel ? (
                  <OLTabs
                    items={tierSetItems?.map((item: TierSetItem) => {
                      return {
                        name: item.label,
                        id: item.id,
                      };
                    })}
                    value={selectedTierSetId}
                    onChange={(_, tab: string) => {
                      setSelectedTierSetId(tab);
                    }}
                  />
                ) : (
                  <Skeleton height={100} />
                )}
              </Grid>
              <OLPaper noPadding>
                <Grid container rowGap={16} className={classes.container}>
                  {tierSetTableData.map((item, index: number) => (
                    <Grid className={classes.item} item key={index} xs={12 / 5}>
                      <div className={classes.label}>
                        {item.icon && item.icon}
                        <BoxLabel
                          dataTestId={item.labelDataTestId}
                          label={message({ id: item.label })}
                        />
                        {item.tooltip && (
                          <Tooltip text={item.tooltip}>
                            <HelpCircleIcon className={classes.tooltipIcon} />
                          </Tooltip>
                        )}
                      </div>
                      <BoxValue
                        dataTestId={item.dataTestId}
                        value={item.value}
                      />
                    </Grid>
                  ))}
                </Grid>
                <div className={classes.divider} />
                <Grid container className={classes.conditionsSection}>
                  {renderedConditions?.map((condition, index) => (
                    <Grid
                      key={condition.conditionId}
                      item
                      xs={12}
                      className={classes.conditionItem}
                    >
                      <Typography
                        className={classes.typography}
                        data-test-id={`condition-${index}-label`}
                      >
                        {prepareConditionInputName(
                          condition.attribute,
                          condition.walletType,
                        )}
                      </Typography>
                      <Box
                        className={`${classes.conditionValueWrapper} ${classes.gap}`}
                        data-test-id={`condition-${index}-value`}
                      >
                        <Box className={classes.conditionValueWrapper}>
                          <Typography
                            className={`${classes.typography} ${classes.boldFont}`}
                          >
                            {(typeof condition.currentValue === "number" &&
                              condition.currentValue) ||
                              "-"}
                          </Typography>
                          {isNextTier && (
                            <Typography className={classes.typography}>
                              /{condition.valueGoal}
                            </Typography>
                          )}
                        </Box>
                        {condition.currentValue ? (
                          <BarChartPercentage
                            barChartData={[
                              {
                                itemShare: condition.currentValue,
                              },
                              {
                                itemShare:
                                  condition.valueGoal - condition.currentValue,
                              },
                            ]}
                            className={classes.barChart}
                          />
                        ) : null}
                      </Box>
                    </Grid>
                  ))}
                  {conditionsToRender!?.length > 0 ? (
                    <OLButton
                      className={classes.showAllBtn}
                      onClick={handleShowAllClick}
                      data-test-id="show-all"
                    >
                      <PlusIcon className={classes.addIcon} />
                      {message(
                        { id: "showAllElements" },
                        {
                          elements: conditionsToRender!.length,
                        },
                      )}
                    </OLButton>
                  ) : null}
                </Grid>
              </OLPaper>
            </>
          ) : null}
        </>
      ) : (
        <Grid className={classes.chartsWrapper} container spacing={10}>
          <Grid sm={12} item>
            <ChartBox
              header={membersStatus?.levelName || "-"}
              subheader={message({ id: "currentTier" })}
              className={classes.textWrapper}
              table={[
                ...(settings?.tierAssignType === "points"
                  ? [
                      {
                        label: message({ id: "pointsToTheNextTier" }),
                        value: membersStatus?.pointsToNextLevel,
                      },
                    ]
                  : []),
                ...(settings?.tierAssignType === "transactions"
                  ? [
                      {
                        label: message({ id: "spendingToTheNextTier" }),
                        value:
                          !!settings?.excludeDeliveryCostsFromTierAssignment
                            ? membersStatus?.transactionsAmountToNextLevelWithoutDeliveryCosts
                            : membersStatus?.transactionsAmountToNextLevel,
                      },
                    ]
                  : []),
                {
                  label: message({ id: "tierPromotionDate" }),
                  value: OLDate(membersStatus?.levelAchievementDate, {
                    formatString: "dd MMM, yyy",
                  }),
                },
                ...(settings?.levelDowngradeMode === "after_x_days"
                  ? [
                      {
                        label: message({ id: "tierResetDate" }),
                        value: membersStatus?.levelWillExpireInDays
                          ? format(
                              Date.parse(
                                `${addDays(
                                  new Date(),
                                  membersStatus.levelWillExpireInDays,
                                )}`,
                              ),
                              "dd MMM, yyy",
                            )
                          : "-",
                      },
                    ]
                  : []),
              ]}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
}
